import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, Users } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const CTASection = () => {
  const t = useTr();
  const { ref, inView } = useInView(0.12);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then(r => setCount(r.data.count)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      if (res.data.success) {
        setStatus('success');
        setMessage(res.data.message);
        setEmail('');
        setCount(c => c !== null ? c + 1 : null);
      } else {
        setStatus('duplicate');
        setMessage(res.data.message);
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <section id="waitlist" className="py-20 sm:py-24 md:py-32 relative" style={{ zIndex: 10, background: 'var(--sb-bg-4)' }} data-testid="cta-section">
      {/* Animated radial glow */}
      <div className="radial-pulse" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '80%', maxWidth: 700, height: 400, background: 'radial-gradient(ellipse, rgba(20,184,166,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
      {/* Grid overlay */}
      <div className="absolute inset-0 line-grid opacity-30 pointer-events-none" />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 md:px-10 text-center relative">
        <div ref={ref} className={`anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider mx-auto" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-4">{t('SwarmBase Protocol', '프로토콜')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5 sm:mb-6">
            {t('Enter the Swarm.', '스웜에 합류하세요.')}<br />
            <span className="gradient-text">{t('Start Building.', '지금 시작하세요.')}</span>
          </h2>
          <p className="text-white/50 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            {t('Deploy, coordinate, and scale autonomous AI agent swarms on decentralized infrastructure. The dApp is live — dive in.',
               'SwarmBase에서 자율 AI 에이전트 스웜을 배포하고, 조율하며, 확장하세요. dApp이 지금 운영 중입니다.')}
          </p>

          {/* Primary CTA — Open dApp */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <a
              href="https://core.swarmbase.io"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-btn inline-flex items-center gap-2 px-8 py-4 rounded font-bold text-base transition-all duration-300"
              style={{ background: '#30d5c8', color: '#000000', boxShadow: '0 0 30px rgba(48,213,200,0.4)' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#55d532'; e.currentTarget.style.boxShadow = '0 0 30px rgba(85,213,50,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#30d5c8'; e.currentTarget.style.boxShadow = '0 0 30px rgba(48,213,200,0.4)'; }}
              data-testid="cta-open-dapp-btn"
            >
              {t('Open dApp', 'dApp 열기')} <ArrowRight size={16} />
            </a>
          </div>

          {/* Secondary — email notify */}
          <div className="max-w-lg mx-auto">
            <p className="text-white/30 text-xs font-mono mb-3 uppercase tracking-widest">{t('Or get notified about updates', '또는 업데이트 알림 받기')}</p>

          {/* Email form */}
          {status === 'success' ? (
            <div className="glass rounded-2xl p-7 sm:p-8 max-w-md mx-auto border border-[#30d5c8]/30 swarm-glow" data-testid="waitlist-success-message">
              <div className="w-12 h-12 rounded-full bg-[#30d5c8]/20 border border-[#30d5c8]/50 flex items-center justify-center mx-auto mb-4">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10l4 4 8-8" stroke="#30d5c8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <h3 className="font-outfit font-bold text-white text-xl mb-2">You're In.</h3>
              <p className="text-white/55 text-sm">{message}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="waitlist-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('your@email.com', '이메일 주소를 입력하세요')}
                required
                disabled={status === 'loading'}
                className="flex-1 px-5 py-3.5 rounded-lg text-white text-sm outline-none disabled:opacity-50 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'JetBrains Mono, monospace' }}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(48,213,200,0.5)'; e.target.style.boxShadow = '0 0 0 2px rgba(48,213,200,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                data-testid="waitlist-email-input"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="glow-btn inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 text-white font-semibold text-sm rounded-lg hover:bg-[#30d5c8]/20 transition-all duration-300 disabled:opacity-50 whitespace-nowrap border border-white/15"
                data-testid="waitlist-submit-btn"
              >
                {status === 'loading' ? t('Saving...', '등록 중...') : <><span>{t('Notify Me', '알림 신청')}</span><ArrowRight size={14} /></>}
              </button>
            </form>
          )}

          {(status === 'error' || status === 'duplicate') && (
            <p className={`mt-3 text-sm font-mono ${status === 'duplicate' ? 'text-[#30d5c8]' : 'text-red-400'}`} data-testid="waitlist-error-message">
              {message}
            </p>
          )}
          </div>
          <p className="text-white/20 text-xs font-mono mt-5">No spam. Unsubscribe anytime.</p>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
